/**
 * AlertsPage — Sprint 5.9 (Alertas + Notificações).
 *
 * Aba "Alertas" no NOC. Eventos críticos que exigem ação.
 *  - Cards: Abertos · Críticos · Hoje · ACK.
 *  - Tabela: Data · Severidade · Origem · Mensagem · Canal · Status.
 *  - Ações por alerta: ACK · Fechar.
 *  - Botão "Testar" — dispara um alerta de teste num canal.
 *  - Filtros: severity · status · entity.
 *
 * Polling de 15s.
 */
import { useCallback, useEffect, useState } from "react";
import {
  ackAlert,
  listAlerts,
  listNotifiers,
  scanAlerts,
  testAlert,
} from "../lib/api.js";
import { formatRelative } from "../lib/format.js";
import { t } from "../i18n/index.js";

const POLL_MS = 15000;

const ENTITY_TYPES = [
  "device", "health", "work_order", "inventory", "admin",
];
const CHANNELS = ["internal", "telegram", "email", "webhook"];

// S3.11 — abas de estado (separam Abertos/Críticos/Reconhecidos/Fechados).
const ALERT_TABS = [
  { id: "open", label: "Abertos", q: { status: "open" }, emptyWord: "aberto" },
  { id: "critical", label: "Críticos", q: { severity: "critical" }, emptyWord: "crítico" },
  { id: "ack", label: "Reconhecidos", q: { status: "ack" }, emptyWord: "reconhecido" },
  { id: "closed", label: "Fechados", q: { status: "closed" }, emptyWord: "fechado" },
  { id: "all", label: "Todos", q: {}, emptyWord: "" },
];

const SEV_STYLE = {
  info: "text-noc-accent bg-noc-accent/10 border-noc-accent/30",
  warning: "text-noc-warn bg-noc-warn/10 border-noc-warn/30",
  critical: "text-noc-err bg-noc-err/10 border-noc-err/30",
};
const STATUS_STYLE = {
  open: "text-noc-err",
  ack: "text-noc-warn",
  closed: "text-noc-muted",
};

function SevPill({ severity }) {
  const cls = SEV_STYLE[severity] || "text-noc-muted bg-noc-bg/40";
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${cls}`}>
      {t("alerts.sev_" + severity) || severity}
    </span>
  );
}

export default function AlertsPage({ onOpenDevice }) {
  const [data, setData] = useState({
    alerts: [], total: 0, open_count: 0, critical_count: 0,
    today_count: 0, ack_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [notice, setNotice] = useState(null);
  const [tab, setTab] = useState("open");
  const [entityType, setEntityType] = useState("");
  const [testModal, setTestModal] = useState(false);

  const load = useCallback((signal) => {
    const tabQ = (ALERT_TABS.find((x) => x.id === tab) || {}).q || {};
    const active = { ...tabQ };
    if (entityType) active.entity_type = entityType;
    listAlerts(active, { signal })
      .then((d) => { setData(d); setErr(null); })
      .catch((e) => {
        if (e?.name !== "AbortError") setErr(e?.message || String(e));
      })
      .finally(() => setLoading(false));
  }, [tab, entityType]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    load(ac.signal);
    const id = setInterval(() => {
      const pollAc = new AbortController();
      load(pollAc.signal);
    }, POLL_MS);
    return () => {
      ac.abort();
      clearInterval(id);
    };
  }, [load]);

  const reload = () => {
    const ac = new AbortController();
    load(ac.signal);
  };

  async function doAck(alert, action) {
    try {
      await ackAlert(alert.id, action, "noc-admin");
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  async function doScan() {
    try {
      const res = await scanAlerts();
      const s = res.scanned || {};
      const sum = Object.values(s).reduce((a, b) => a + b, 0);
      setNotice(t("alerts.scanDone") + ` (${sum})`);
      reload();
    } catch (e) {
      setErr(e?.message || String(e));
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-noc-text">
          {t("alerts.title")}
        </h1>
        <div className="flex gap-2">
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={doScan}
          >
            {t("alerts.btnScan")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-border
                       hover:bg-noc-bg/40 text-noc-accent"
            onClick={() => setTestModal(true)}
          >
            {t("alerts.btnTest")}
          </button>
        </div>
      </div>

      {/* Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <DashCard label={t("alerts.cardOpen")}
                  value={data.open_count} tone="red" />
        <DashCard label={t("alerts.cardCritical")}
                  value={data.critical_count} tone="red" />
        <DashCard label={t("alerts.cardToday")}
                  value={data.today_count} tone="cyan" />
        <DashCard label={t("alerts.cardAck")}
                  value={data.ack_count} tone="amber" />
      </div>

      {/* S3.11 — abas de estado + filtro de origem */}
      <div className="flex gap-2 flex-wrap items-center">
        <nav className="flex gap-1 border-b border-noc-border/40">
          {ALERT_TABS.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px ${
                tab === tb.id
                  ? "border-noc-ok/30 text-noc-ok"
                  : "border-transparent text-noc-muted hover:text-noc-text"
              }`}
            >
              {tb.label}
            </button>
          ))}
        </nav>
        <span className="flex-1" />
        <FilterSelect label={t("alerts.filterEntity")}
          value={entityType}
          onChange={setEntityType}
          options={ENTITY_TYPES} tkey="alerts.entity_" />
      </div>

      {err && (
        <div className="text-xs text-noc-err bg-noc-err/10 border
                        border-noc-err/30 rounded p-2">{err}</div>
      )}
      {notice && (
        <div className="text-xs text-noc-ok bg-noc-ok/10
                        border border-noc-ok/30 rounded p-2">
          {notice}
        </div>
      )}

      {/* Tabela */}
      <div className="overflow-x-auto card-pad">
        <table className="w-full text-xs">
          <thead className="text-noc-muted border-b border-noc-border">
            <tr>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colDate")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colSeverity")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colSource")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colMessage")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colChannel")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colStatus")}
              </th>
              <th className="text-left font-medium py-1.5 px-2">
                {t("alerts.colActions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="text-sm text-noc-muted/70
                italic py-3 px-2">{t("common.loading")}</td></tr>
            )}
            {!loading && data.alerts.length === 0 && (
              <tr><td colSpan={7} className="py-8 px-2 text-center">
                <div className="text-2xl mb-1">✓</div>
                <div className="text-sm text-noc-muted">
                  Nenhum alerta {(ALERT_TABS.find((x) => x.id === tab) || {}).emptyWord || ""} no momento.
                </div>
              </td></tr>
            )}
            {data.alerts.map((a) => (
              <tr key={a.id}
                  className="border-b border-noc-border/40
                             hover:bg-noc-bg/30">
                <td className="py-1.5 px-2 text-noc-muted whitespace-nowrap">
                  {formatRelative(a.last_trigger_at)}
                  {a.trigger_count > 1 && (
                    <span className="text-[10px] text-noc-warn ml-1">
                      ×{a.trigger_count}
                    </span>
                  )}
                </td>
                <td className="py-1.5 px-2">
                  <SevPill severity={a.severity} />
                </td>
                <td className="py-1.5 px-2">
                  <span className="text-noc-text">{a.entity_type}</span>
                  {a.device_id && (onOpenDevice ? (
                    <button
                      type="button"
                      onClick={() => onOpenDevice(a.device_id)}
                      className="font-mono text-[10px] ml-1 text-noc-accent
                                 hover:text-noc-accent underline decoration-dotted"
                      title="Abrir detalhe do dispositivo"
                    >
                      {a.device_id}
                    </button>
                  ) : (
                    <span className="text-noc-muted/70 font-mono text-[10px] ml-1">
                      {a.device_id}
                    </span>
                  ))}
                </td>
                <td className="py-1.5 px-2 text-noc-text">
                  <div className="font-medium">{a.title}</div>
                  {a.message && (
                    <div className="text-[10px] text-noc-muted/70">
                      {a.message}
                    </div>
                  )}
                </td>
                <td className="py-1.5 px-2 text-noc-muted">
                  {a.channel}
                </td>
                <td className={"py-1.5 px-2 "
                  + (STATUS_STYLE[a.status] || "")}>
                  {t("alerts.status_" + a.status) || a.status}
                </td>
                <td className="py-1.5 px-2">
                  {a.status !== "closed" && (
                    <div className="flex gap-1">
                      {a.status === "open" && (
                        <button
                          className="text-[10px] px-1.5 py-0.5 rounded
                                     border border-noc-warn/30
                                     text-noc-warn hover:bg-noc-warn/10"
                          onClick={() => doAck(a, "ack")}
                          title="Reconhecer: marca como visto/em tratamento (continua aberto até ser fechado)"
                        >
                          {t("alerts.btnAck")}
                        </button>
                      )}
                      <button
                        className="text-[10px] px-1.5 py-0.5 rounded
                                   border border-noc-border
                                   hover:bg-noc-bg/50"
                        onClick={() => doAck(a, "close")}
                        title="Fechar: encerra o alerta (sai da lista de abertos)"
                      >
                        {t("alerts.btnClose")}
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-noc-muted/70">
        {t("alerts.showing")}: {data.alerts.length}
      </div>

      {testModal && (
        <TestModal
          onClose={() => setTestModal(false)}
          onDone={(msg) => {
            setTestModal(false);
            setNotice(msg);
            reload();
          }}
        />
      )}
    </div>
  );
}

function DashCard({ label, value, tone }) {
  const toneCls = {
    red: "border-noc-err/30 text-noc-err",
    cyan: "border-noc-accent/30 text-noc-accent",
    amber: "border-noc-warn/30 text-noc-warn",
  }[tone] || "border-noc-border text-noc-text";
  return (
    <div className={`rounded border ${toneCls} bg-noc-bg/30 p-2`}>
      <div className="font-mono text-lg font-bold">{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-noc-faint">{label}</div>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, tkey }) {
  return (
    <div>
      <label className="text-[10px] text-noc-muted block mb-0.5">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-xs bg-noc-bg/60 border border-noc-border
                   rounded px-2 py-1 text-noc-text"
      >
        <option value="">— todos —</option>
        {options.map((o) => (
          <option key={o} value={o}>{t(tkey + o) || o}</option>
        ))}
      </select>
    </div>
  );
}

/** Modal de teste de canal — dispara um alerta de teste. */
function TestModal({ onClose, onDone }) {
  const CHANNELS = ["internal", "telegram", "email", "webhook"];
  const [channel, setChannel] = useState("internal");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [notifiers, setNotifiers] = useState([]);

  useEffect(() => {
    const ac = new AbortController();
    listNotifiers({ signal: ac.signal })
      .then(setNotifiers)
      .catch(() => {});
    return () => ac.abort();
  }, []);

  async function submit() {
    setBusy(true);
    setResult(null);
    try {
      const res = await testAlert({
        channel, severity: "info",
        title: "Alerta de teste",
        message: "Disparo de teste do canal " + channel,
      });
      setResult(res);
      if (res.delivered) {
        onDone(t("alerts.testOk") + ` (${channel})`);
      }
    } catch (e) {
      setResult({ delivered: false, detail: e?.message || String(e) });
    } finally {
      setBusy(false);
    }
  }

  const notifState = notifiers.find((n) => n.channel === channel);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center
                    justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-noc-bg border border-noc-border rounded-lg
                      p-4 w-full max-w-sm space-y-3"
           onClick={(e) => e.stopPropagation()}>
        <h3 className="text-sm font-bold text-noc-text">
          {t("alerts.btnTest")}
        </h3>
        <div>
          <label className="text-[11px] text-noc-muted block mb-0.5">
            {t("alerts.colChannel")}
          </label>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            className="w-full text-xs bg-noc-bg/60 border
                       border-noc-border rounded px-2 py-1 text-noc-text"
          >
            {CHANNELS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        {notifState && (
          <div className="text-[10px] text-noc-muted">
            {notifState.enabled
              ? (notifState.configured
                  ? t("alerts.channelReady")
                  : t("alerts.channelNotConfigured"))
              : t("alerts.channelDisabled")}
          </div>
        )}
        {result && (
          <div className={"text-[11px] rounded p-1.5 border "
            + (result.delivered
                ? "text-noc-ok bg-noc-ok/10 border-noc-ok/30"
                : "text-noc-warn bg-noc-warn/10 border-noc-warn/30")}>
            {result.detail}
          </div>
        )}
        <div className="flex gap-2 justify-end">
          <button
            className="text-xs px-2.5 py-1 rounded border
                       border-noc-border text-noc-muted hover:bg-noc-bg/40"
            onClick={onClose}
          >
            {t("common.cancel")}
          </button>
          <button
            className="text-xs px-2.5 py-1 rounded border border-noc-accent/30
                       text-noc-accent bg-noc-accent/10 hover:bg-noc-accent/10
                       disabled:opacity-50"
            disabled={busy}
            onClick={submit}
          >
            {busy ? t("common.loading") : t("alerts.btnTest")}
          </button>
        </div>
      </div>
    </div>
  );
}
